import React from "react";
import { Table, Tag, Space, Button, Popconfirm, Switch } from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import type { RoomPackage } from "../../../types";

const CHARGE_BASIS_LABELS: Record<string, string> = {
  per_room_per_night: "Per Room/Night",
  per_person_per_night: "Per Person/Night",
  per_stay: "Per Stay",
  per_person_per_stay: "Per Person/Stay",
  complimentary: "Complimentary",
};

interface RoomPackageTableProps {
  packages: RoomPackage[];
  loading: boolean;
  canManage: boolean;
  onView: (pkg: RoomPackage) => void;
  onEdit: (pkg: RoomPackage) => void;
  onDelete: (id: string) => void;
}

const RoomPackageTable: React.FC<RoomPackageTableProps> = ({
  packages,
  loading,
  canManage,
  onView,
  onEdit,
  onDelete,
}) => {
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 100,
      render: (code: string) => (
        <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{code}</span>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (category: string) =>
        category ? <Tag>{category}</Tag> : "-",
    },
    {
      title: "Charge Basis",
      dataIndex: "charge_basis",
      key: "charge_basis",
      width: 150,
      render: (basis: string) => CHARGE_BASIS_LABELS[basis] || basis,
    },
    {
      title: "Base Price",
      dataIndex: "base_price",
      key: "base_price",
      width: 120,
      align: "right" as const,
      render: (price: number) =>
        `LKR ${Number(price).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    },
    {
      title: "Price/Person",
      dataIndex: "price_per_person",
      key: "price_per_person",
      width: 130,
      align: "right" as const,
      render: (price: number) =>
        `LKR ${Number(price).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    },
    {
      title: "Order",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 70,
      align: "center" as const,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      align: "center" as const,
      render: (active: boolean) => <Switch checked={active} disabled size="small" />,
    },
    ...(canManage
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: RoomPackage) => (
              <Space size="small">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => onView(record)}
                />
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(record)}
                />
                <Popconfirm
                  title="Delete this package?"
                  onConfirm={() => onDelete(record.id)}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={packages}
      loading={loading}
      pagination={{ pageSize: 20 }}
      size="middle"
    />
  );
};

export default RoomPackageTable;
